import { Component, ChangeDetectionStrategy } from '@angular/core';
import { AppTableBaseComponent } from '@components/tables/app-table-base.component';
import { tuainFormsBaseModules, tuainBaseComponents } from '@components/tuain.modules';

/**
 * Estados en los que la solicitud fue devuelta al titular para que corrija o complete su
 * documentación. Espejan `DOCUMENT_FIX_STATUS` del gateway
 * (`app/models/forms/common/credits/my-credits.factory.js`), que para este mismo par muestra
 * «En subsanación» en el campo `statusName` de la tarjeta.
 */
const DOCUMENT_FIX_STATUS = ['userDocumentFix', 'userDocumentFix2'];
const DOCUMENT_FIX_ACTION_TITLE = 'Corregir documentos';
const UPLOAD_ACTION_ICON = 'upload';

/** Acción que ocupa el renglón principal de la tarjeta. */
const MAIN_ACTION_CODE = 'viewDetail';

@Component({
  standalone: true,
  selector: 'app-credit-requests-table',
  imports: [...tuainFormsBaseModules, ...tuainBaseComponents],
  templateUrl: './credit-requests-table.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./credit-requests-table.component.scss'],
  // changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreditRequestsTableComponent extends AppTableBaseComponent {
  cardActions: any = {};
  rowFields: any[];
  ammountField: any[];

  override start() {
    super.start();
    this.inlineActions.forEach(act => {
      this.cardActions[act.actionCode] = act;
    });
    this.rowFields = this.customAttributes()?.['rowFields'] ?? [];
    this.ammountField = this.customAttributes()?.['ammountField'] ?? [];
  }

  override tableActionSelected(event) {
    super.tableActionSelected(event);
  }

  /** La solicitud está devuelta al titular esperando que corrija o complete documentos. */
  isDocumentFix(record: any): boolean {
    return DOCUMENT_FIX_STATUS.includes(record?.recordData?.status);
  }

  /**
   * La solicitud espera un cargue del titular. Lo decide el gateway con `mustUploadDocuments`
   * (misma lista de estados con la que el formulario de cargue habilita las filas: cargue inicial,
   * subsanaciones y cargue de legalización tras aceptar la oferta); los dos estados de subsanación
   * se conservan como respaldo para un gateway que aún no publique la bandera. El `BOOLEAN` puede
   * llegar como texto.
   */
  isUploadPending(record: any): boolean {
    const mustUpload = record?.recordData?.mustUploadDocuments;
    return mustUpload === true || mustUpload === 'true' || this.isDocumentFix(record);
  }

  isMainAction(action: any): boolean {
    return action?.actionCode === MAIN_ACTION_CODE;
  }

  /**
   * Con un cargue pendiente, «Ver detalle» es en realidad la puerta al cargue de documentos: el
   * gateway ya resuelve `nextForm` a `creditRequestDocuments` para estos estados. Se presenta
   * entonces como lo que hace —botón azul lleno, texto de cargue e icono de subida— para que la
   * solicitud se distinga del resto del listado y el titular sepa que le toca actuar. El rótulo lo
   * publica el gateway en `uploadActionTitle` («Cargar documentos», «Corregir documentos», «Cargar
   * documentos de legalización»); sin él se cae al de subsanación.
   */
  mainActionTitle(action: any, record: any): string {
    if (!this.isUploadPending(record)) {
      return action?.actionTitle;
    }
    return record?.recordData?.uploadActionTitle || DOCUMENT_FIX_ACTION_TITLE;
  }

  mainActionIcon(action: any, record: any): string {
    return this.isUploadPending(record) ? UPLOAD_ACTION_ICON : action?.iconName;
  }

  mainActionClass(record: any): string {
    return this.isUploadPending(record) ? 'primary' : 'outline';
  }

  /**
   * `fixDocument` («Verificar documentación») lleva al mismo cargue que ya expone el botón
   * principal cuando la solicitud está en subsanación: se omite para no dejar dos llamados a la
   * acción compitiendo en la misma tarjeta.
   */
  isSecondaryActionVisible(action: any, record: any): boolean {
    if (this.isMainAction(action)) {
      return false;
    }
    if (action?.actionCode === 'fixDocument' && this.isUploadPending(record)) {
      return false;
    }
    return this.isActionVisible(action, record);
  }
}
